import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Plus, Trash2 } from "lucide-react";
import api from "../../../services/api";
import { Table } from "../../../components/table";
import { BackButton } from "../../../components/button/back-button";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { useToast } from "../../../contexts/toast/toast-context";
import { formatUserPhone, getApiErrorMessage } from "../../../utils";
import { useDebounce } from "../../../hooks/use-debounce";
import { COMMERCIAL_STATUS_META, type User } from "../../../types/user";

type FindAllUserDto = {
  data: User[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export default function ListClientes() {
  const { success, error } = useToast();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const getUsers = useCallback(
    async (pageNumber = 1, searchValue = debouncedSearch) => {
      try {
        const response = await api.get<FindAllUserDto>("/user", {
          params: { page: pageNumber, limit, search: searchValue },
        });

        setUsers(response.data.data);
        setPage(response.data.page);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
      } catch (err) {
        const apiMessage = getApiErrorMessage(err);
        error(apiMessage);
      }
    },
    [debouncedSearch, error, limit],
  );

  useEffect(() => {
    getUsers(1, debouncedSearch);
  }, [debouncedSearch, getUsers]);

  async function deleteUser(id: string) {
    if (isDeleting) return;
    try {
      setIsDeleting(true);
      await api.delete(`/user/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setUserToDelete(null);
      success("Contato removido com sucesso!");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsDeleting(false);
    }
  }

  const columns = [
    { key: "userName", label: "Nome" },
    {
      key: "dialCode",
      label: "Telefone",
      render: (user: User) => formatUserPhone(user) || "-",
    },
    {
      key: "email",
      label: "Email",
      render: (user: User) => user.email || "-",
    },
    {
      key: "commercialStatus",
      label: "Status comercial",
      render: (user: User) => {
        const meta =
          COMMERCIAL_STATUS_META[user.commercialStatus ?? "UNQUALIFIED"];
        return <span className={meta.className}>{meta.label}</span>;
      },
    },
    {
      key: "additionalInfo",
      label: "Informações adicionais",
      render: (user: User) => user.additionalInfo || "-",
    },
    {
      key: "actions",
      label: "Ações",
      render: (user: User) => (
        <div className="text-center flex justify-center gap-3">
          <button
            onClick={() => navigate(`/painel/contatos/visualizar/${user.id}`)}
            className="text-slate-700 hover:text-slate-900 cursor-pointer"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => setUserToDelete(user)}
            className="btn-link-danger cursor-pointer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="app-page">
      <BackButton />
      <div className="flex items-center justify-between mb-6">
        <h1 className="app-page-title">Contatos</h1>
        <button
          onClick={() => navigate("/painel/contatos/cadastro")}
          className="btn btn-neutral cursor-pointer"
        >
          <Plus className="w-4 h-4" /> Novo Contato
        </button>
      </div>

      <div className="mb-4">
        <input
          className="border p-2 rounded w-full max-w-md"
          type="text"
          placeholder="Buscar por nome ou telefone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Table<User>
        columns={columns}
        data={users}
        page={page}
        total={total}
        totalPages={totalPages}
        onPageChange={(newPage) => getUsers(newPage, debouncedSearch)}
      />

      <ConfirmationModal
        isOpen={!!userToDelete}
        title="Confirmar exclusão"
        description={
          userToDelete
            ? `Tem certeza que deseja excluir o contato ${userToDelete.userName}?`
            : ""
        }
        onCancel={() => setUserToDelete(null)}
        onConfirm={() => userToDelete && deleteUser(userToDelete.id!)}
        isLoading={isDeleting}
      />
    </div>
  );
}
